import { useState, type FormEvent, type KeyboardEvent } from "react";
import { IonContent, IonPage } from "@ionic/react";
import { useHistory } from "react-router-dom";

import { ActionBar } from "../components/ActionBar";
import { Field } from "../components/Fields";
import { Mark } from "../components/Mark";
import { Notice } from "../components/Notice";
import { Segmented } from "../components/Segmented";
import { Screen } from "../components/Shell";
import { useKaya } from "../store/kayaStore";
import { ApiError } from "../sync/api";
import "./Login.css";

const eye = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 12s3.6-7 10-7 10 7 10 7-3.6 7-10 7S2 12 2 12z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);
const eyeOff = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 3l18 18M10.6 5.1A10 10 0 0 1 12 5c6.4 0 10 7 10 7a17 17 0 0 1-3.2 4.1M6.6 6.6A17 17 0 0 0 2 12s3.6 7 10 7a9.6 9.6 0 0 0 5.4-1.6M9.9 9.9a3 3 0 0 0 4.2 4.2" />
  </svg>
);

type Mode = "in" | "create";

export default function Login() {
  const history = useHistory();
  const { login, register } = useKaya();

  const [mode, setMode] = useState<Mode>("in");
  const creating = mode === "create";
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit() {
    if (busy) return;
    // The primary is docked outside the form, so the browser's `required` check never runs.
    if (!email.trim() || !password) {
      setErr("Enter your email and password.");
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      if (creating) await register(email.trim(), password, name.trim());
      else await login(email.trim(), password);
      // Where to go is the gate's decision, not this form's — a brand-new account is not
      // approved yet and has no home to land on.
      history.replace("/today");
    } catch (ex) {
      setErr(
        ex instanceof ApiError || ex instanceof Error ? ex.message : "Something went wrong — try again"
      );
    } finally {
      setBusy(false);
    }
  }

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void submit();
  };
  // Enter in any box submits, as it would with a submit button inside the form.
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Enter" && e.target instanceof HTMLInputElement) {
      e.preventDefault();
      void submit();
    }
  };

  return (
    <IonPage>
      <IonContent>
        <Screen>
          <div className="auth">
            <div className="auth-head">
              <div className="auth-mark">
                <Mark />
              </div>
              <div>
                <h1 className="auth-title">Kaya</h1>
                <p className="auth-sub">
                  {creating
                    ? "New accounts are checked by hand before they open."
                    : "Sign in to pick up where you left off."}
                </p>
              </div>
            </div>

            <Segmented<Mode>
              ariaLabel="Sign in or create an account"
              value={mode}
              options={[
                { value: "in", label: "Sign in" },
                { value: "create", label: "Create account" },
              ]}
              onChange={(m) => {
                setMode(m);
                setErr(null);
              }}
            />

            {err ? (
              <div role="alert">
                <Notice tone="danger">{err}</Notice>
              </div>
            ) : null}

            <form className="auth-form" onSubmit={onSubmit} onKeyDown={onKey} noValidate>
              {creating ? (
                <Field label="Name" type="text" autoComplete="name" value={name} onChange={setName} />
              ) : null}
              <Field
                label="Email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={setEmail}
                invalid={!!err && !email.trim()}
              />
              <Field
                label="Password"
                type={show ? "text" : "password"}
                autoComplete={creating ? "new-password" : "current-password"}
                value={password}
                onChange={setPassword}
                invalid={!!err && !password}
                trailing={
                  <button
                    type="button"
                    className="k-iconbtn"
                    onClick={() => setShow((s) => !s)}
                    aria-label={show ? "Hide password" : "Show password"}
                    aria-pressed={show}
                  >
                    {show ? eyeOff : eye}
                  </button>
                }
              />
            </form>

            <p className="auth-foot">
              Accounts are approved by the owner. What you log stays on this phone and is copied to
              your account, so a new phone can pick it up.
            </p>
          </div>

          <ActionBar
            primary={{
              label: busy ? "Just a moment…" : creating ? "Create account" : "Sign in",
              onClick: () => void submit(),
              busy,
            }}
          />
        </Screen>
      </IonContent>
    </IonPage>
  );
}
